/**
 * =============================================================================
 *  SO DO TRANG - MO HINH DUY NHAT CHO MOI THAO TAC CAP TRANG
 * =============================================================================
 *
 *  Mot "so do trang" (PagePlan) la danh sach o trang cua tai lieu HIEN THI. Moi
 *  o chi noi ba dieu: trang nay lay tu tai lieu nguon nao, o chi so nao trong
 *  tai lieu do, va xoay them bao nhieu do so voi ban goc.
 *
 *      so do trang = replay(so do ban dau, danh sach EditCommand cap trang)
 *
 *  Day la ADR-003 ap dung cho thao tac trang: byte goc bat bien, moi thao tac
 *  la mot lenh co ngu nghia, va tai lieu that chi duoc dung len o save().
 *
 * =============================================================================
 *  CAI BAY THU TU - LY DO TEP NAY TON TAI THAY VI SUA BYTE TRUC TIEP
 * =============================================================================
 *  Lenh cap trang PHU THUOC VI TRI. "Xóa trang 2" roi "xóa trang 5" khong giong
 *  "xóa trang 5" roi "xóa trang 2": sau lenh dau, moi trang phia sau da dich
 *  chi so.
 *
 *  Neu save() sua truc tiep len byte dang co, bam Luu hai lan se ap dung ca
 *  danh sach HAI LAN - xoay 180 do thay vi 90, xoa hai lan so trang.
 *
 *  Cach o day loai bo han lop loi do:
 *    - replay LUON bat dau tu so do ban dau (dung so trang cua tai lieu goc),
 *    - replay la ham THUAN TUY, khong cham vao byte,
 *    - vat chat hoa (materialize) chay DUNG MOT LAN o cuoi, tu byte goc.
 *  Nho vay replay(cmds) cho ra cung mot so do o moi lan goi, va hai lan luu
 *  lien tiep cho ra hai luong byte giong het nhau.
 *
 * =============================================================================
 *  TRUNG THUC VE MAT MAT
 * =============================================================================
 *  pdf-lib sao chep TRANG, khong sao chep cau truc cap tai lieu. Xoa, sap xep
 *  lai hay tach trang se lam mat: bookmark (outline), lien ket noi bo tro toi
 *  trang khac, va cay cau truc the (tagged PDF / PDF-UA). Chu thich nam TREN
 *  trang thi duoc giu.
 *
 *  Day khong phai loi co the vá o tang nay - no doi mot bo viet lai bang tham
 *  chieu cap tai lieu ma pdf-lib khong co API. Giao dien PHAI noi ro dieu nay
 *  MOT LAN cho nguoi dung (xem PAGE_OPS_FIDELITY_WARNING), khong duoc lang le
 *  coi thao tac la khong mat mat.
 */
import type { EditCommand } from '@pdfeditor/types';

/** Ma cua tai lieu dang mo. Moi so do ban dau deu tro ve day. */
export const ORIGINAL_DOC_ID = '@original';

/** Kich thuoc A4 theo diem, dung khi khong suy ra duoc tu trang lang gieng. */
export const A4_PT = { widthPt: 595.28, heightPt: 841.89 } as const;

export interface BlankSize {
  widthPt: number;
  heightPt: number;
}

/**
 * Mot o trang trong tai lieu hien thi.
 *
 * `sourcePageIndex === null` nghia la trang trang. Kich thuoc cua no la 'auto'
 * (theo trang lang gieng) hoac mot cap so cu the. 'auto' duoc chon lam mac dinh
 * vi no la thu DAO NGUOC DUOC: hop dong kieu chi cho phep `{ kind: 'blank' }`
 * khong kem kich thuoc, nen mot trang trang co kich thuoc rieng se mat kich
 * thuoc do khi hoan tac roi lam lai. 'auto' thi khong mat gi.
 */
export interface PageSlot {
  docId: string;
  sourcePageIndex: number | null;
  /** Xoay CONG THEM so voi trang nguon: 0 | 90 | 180 | 270. */
  rotationDelta: number;
  blankSize?: BlankSize | 'auto';
}

export type PagePlan = readonly PageSlot[];

/** So do ban dau: tai lieu goc, dung thu tu, khong xoay them. */
export function buildInitialPlan(pageCount: number): PagePlan {
  const plan: PageSlot[] = [];
  for (let i = 0; i < pageCount; i++) {
    plan.push({ docId: ORIGINAL_DOC_ID, sourcePageIndex: i, rotationDelta: 0 });
  }
  return plan;
}

/** Cac bien the lenh tac dong len so do trang. */
export type PageCommand = Extract<
  EditCommand,
  { kind: 'page.insert' | 'page.remove' | 'page.reorder' | 'page.rotate' }
>;

export function isPageCommand(command: EditCommand): command is PageCommand {
  return (
    command.kind === 'page.insert' ||
    command.kind === 'page.remove' ||
    command.kind === 'page.reorder' ||
    command.kind === 'page.rotate'
  );
}

export type PlanResult =
  | { ok: true; plan: PagePlan }
  | { ok: false; error: string };

/**
 * Ap MOT lenh len so do. Ham thuan tuy: khong sua `plan`, tra ve so do moi.
 *
 * Lenh khong hop le bi TU CHOI chu khong duoc am tham bo qua. Mot lenh bi bo
 * qua khi ap nhung van nam trong danh sach se duoc phat lai o lan luu sau va co
 * the "thanh cong" luc do, cho ra mot tai lieu khac han thu nguoi dung nhin
 * thay tren man hinh.
 */
export function applyPageCommand(plan: PagePlan, command: PageCommand): PlanResult {
  switch (command.kind) {
    case 'page.remove': {
      if (!inRange(command.pageIndex, plan.length)) {
        return { ok: false, error: outOfRange(command.pageIndex, plan.length) };
      }
      if (plan.length === 1) {
        return { ok: false, error: 'Không xóa được trang cuối cùng: PDF phải có ít nhất một trang.' };
      }
      const next = [...plan];
      next.splice(command.pageIndex, 1);
      return { ok: true, plan: next };
    }

    case 'page.insert': {
      // Chen o cuoi la hop le, nen bien tren la length CHU KHONG PHAI length-1.
      if (!Number.isInteger(command.atIndex) || command.atIndex < 0 || command.atIndex > plan.length) {
        return { ok: false, error: outOfRange(command.atIndex, plan.length + 1) };
      }
      const slot: PageSlot =
        command.source.kind === 'blank'
          ? { docId: ORIGINAL_DOC_ID, sourcePageIndex: null, rotationDelta: 0, blankSize: 'auto' }
          : {
              docId: command.source.docId,
              sourcePageIndex: command.source.pageIndex,
              rotationDelta: 0,
            };
      const next = [...plan];
      next.splice(command.atIndex, 0, slot);
      return { ok: true, plan: next };
    }

    case 'page.reorder': {
      if (!inRange(command.from, plan.length)) {
        return { ok: false, error: outOfRange(command.from, plan.length) };
      }
      if (!inRange(command.to, plan.length)) {
        return { ok: false, error: outOfRange(command.to, plan.length) };
      }
      // NGU NGHIA CHOT: "go o `from` ra, roi chen vao vi tri `to` CUA MANG DA
      // NGAN DI". Day khong phai chi tiet tuy y - lenh dao nguoc trong
      // state/commands.ts la { from: to, to: from }, va no CHI dung voi dinh
      // nghia nay. Doi sang "chen truoc phan tu dang o `to`" se lam undo lech
      // mot o khi keo trang xuong duoi. Xem test pageOps.test.ts.
      const next = [...plan];
      const [moved] = next.splice(command.from, 1);
      if (!moved) return { ok: false, error: outOfRange(command.from, plan.length) };
      next.splice(command.to, 0, moved);
      return { ok: true, plan: next };
    }

    case 'page.rotate': {
      if (!inRange(command.pageIndex, plan.length)) {
        return { ok: false, error: outOfRange(command.pageIndex, plan.length) };
      }
      const next = [...plan];
      const slot = next[command.pageIndex] as PageSlot;
      next[command.pageIndex] = {
        ...slot,
        rotationDelta: normalizeAngle(slot.rotationDelta + command.delta),
      };
      return { ok: true, plan: next };
    }

    default:
      return { ok: false, error: 'Lệnh không thuộc nhóm thao tác trang.' };
  }
}

/**
 * Phat lai ca danh sach len so do ban dau.
 *
 * Lenh khong phai cap trang duoc bo qua o day (chung di duong khac). Mot lenh
 * cap trang khong hop le lam CA phep phat lai that bai - vi neu bo qua no thi
 * moi lenh phia sau se tro toi sai trang.
 */
export function replayPagePlan(initial: PagePlan, commands: readonly EditCommand[]): PlanResult {
  let plan = initial;
  for (let i = 0; i < commands.length; i++) {
    const command = commands[i] as EditCommand;
    if (!isPageCommand(command)) continue;
    const step = applyPageCommand(plan, command);
    if (!step.ok) {
      return { ok: false, error: `Lệnh thứ ${i + 1} (${command.kind}) không áp được: ${step.error}` };
    }
    plan = step.plan;
  }
  return { ok: true, plan };
}

/** true khi so do khac hoan toan so do ban dau - dung de bo qua duong vat chat hoa. */
export function planIsIdentity(plan: PagePlan, originalPageCount: number): boolean {
  if (plan.length !== originalPageCount) return false;
  return plan.every(
    (slot, i) =>
      slot.docId === ORIGINAL_DOC_ID && slot.sourcePageIndex === i && slot.rotationDelta === 0,
  );
}

/** Cac ma tai lieu ngoai ma so do nay can co byte de vat chat hoa. */
export function foreignDocIds(plan: PagePlan): string[] {
  const ids = new Set<string>();
  for (const slot of plan) {
    if (slot.sourcePageIndex !== null && slot.docId !== ORIGINAL_DOC_ID) ids.add(slot.docId);
  }
  return [...ids];
}

/**
 * KICH THUOC CUA TRANG TRANG 'auto' - MOT PHEP TINH DUY NHAT.
 *
 * Quy tac: theo trang co noi dung gan nhat phia TRUOC; neu o dau tai lieu chua
 * co trang nao thi theo trang co noi dung dau tien; neu ca so do khong co trang
 * nao co noi dung thi A4.
 *
 * Ham nay nhan mot ham tra kich thuoc thay vi nhan doi tuong trang, vi no phai
 * phuc vu CA HAI phia: duong xuat goi no voi kich thuoc lay tu PDFPage cua
 * pdf-lib, con trinh xem goi no voi kich thuoc lay tu DocumentInfo cua pdf.js.
 * Truoc day chi duong xuat co quy tac nay, nen trinh xem hoac de mot khoang
 * trong hoac lay dai trang lang gieng - tuc la ban xem truoc va tep xuat ra noi
 * hai dieu khac nhau ve cung mot trang. Xem ghi chu dau tep operations.ts.
 *
 * Ham THUAN TUY va TAT DINH: cung so do cho ra cung ket qua o moi lan goi, dieu
 * kien de "luu hai lan ra hai tep giong het nhau" con dung.
 */
export function resolveBlankSizes(
  plan: PagePlan,
  contentSizeAt: (slotIndex: number) => BlankSize | null,
): BlankSize[] {
  const sizes = new Array<BlankSize>(plan.length);
  let lastSeen: BlankSize | null = null;

  for (let i = 0; i < plan.length; i++) {
    const found = contentSizeAt(i);
    if (found) lastSeen = found;
    sizes[i] = lastSeen ?? { ...A4_PT };
  }

  // Cac o trang o DAU so do chua thay trang nao: lay trang co noi dung dau tien.
  let firstSeen: BlankSize | null = null;
  for (let i = 0; i < plan.length; i++) {
    const found = contentSizeAt(i);
    if (found) {
      firstSeen = found;
      break;
    }
  }
  if (firstSeen) {
    for (let i = 0; i < plan.length; i++) {
      if (contentSizeAt(i)) break;
      sizes[i] = firstSeen;
    }
  }

  return sizes;
}

function inRange(index: number, length: number): boolean {
  return Number.isInteger(index) && index >= 0 && index < length;
}

function outOfRange(index: number, length: number): string {
  return `Trang ${index + 1} không tồn tại (tài liệu đang có ${length} trang).`;
}

export function normalizeAngle(angle: number): number {
  return ((Math.round(angle / 90) * 90) % 360 + 360) % 360;
}

/**
 * CANH BAO TRUNG THUC, hien MOT LAN cho moi tai lieu.
 *
 * Dat o day chu khong trong thanh phan giao dien vi no la su that ve DUONG GHI,
 * khong phai ve mot man hinh cu the. Neu sau nay lo Rust/PDFium lam duoc viet
 * lai tham chieu cap tai lieu, cho can sua la day.
 */
export const PAGE_OPS_FIDELITY_WARNING =
  'Thao tác trang ở bản web dựng lại tài liệu từ từng trang. Bookmark (mục lục), ' +
  'liên kết nội bộ trỏ sang trang khác, và cây cấu trúc thẻ (PDF/UA) sẽ KHÔNG được giữ. ' +
  'Nội dung trang và chú thích nằm trên trang thì vẫn nguyên.';
